import { getStoredLanguage, safelyDecodeBase64Url } from '@dev/ee-tools';
import {
    TemplateEditor as Editor,
    EditorSaveData,
    EditorSaveEventHandler,
    EditorTemplateStatus,
    EditorTemplateVersion
} from '@dev/template-editor';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { push as Push } from 'redux-first-history';

import { CampaignCreatorType, TemplatesPaths } from '@/services/dictionaries/Paths';
import { LockIcon } from '@/services/icons/LockedIcon';
import { isIntercomEnabled } from '@/services/Secuirty';
import { parseUrlParams } from '@/services/tools/handleUrlParams';
import { updateTemplateContent } from '@/services/tools/templateEditorParser';
import { CloseTemplateEditor } from '@/store/campaignCreator/actions';
import { GetContactFieldsStart, getCustomFieldsStore } from '@/store/customFields/actions';
import { getDialogStore, HideDialog } from '@/store/dialog/actions';
import { getMediaManagerStore, ShowMediaManager } from '@/store/mediaManager/actions';
import { getPrebuildModulesStore, PrebuildModulesLoadStart } from '@/store/prebuildModules/actions';
import {
    getEditorFilesStore,
    getEditorTemplateStore,
    LoadStart,
    Reset,
    SaveStart,
    SetTemplatePreviewHtml,
} from '@/store/templateEditor/actions';
import { EmojiPickerLocale } from '@dev/ee-components';
import { useBrandColorsInTemplateEditor } from '@/services/hooks/useBrandColorsInTemplateEditor';
import { useEmailTemplateSpecialFields } from '@/services/hooks/useEmailTemplateSpecialFields';
import { EditorMediaManager } from './components/EditorMediaManager';
import { PreviewOverlay } from './components/PreviewOverlay';
import { SavedLockedInfo } from './components/SavedLockedInfo';
import { SavedModulesLimitInfo } from './components/SavedModulesLimitInfo';
import { UpsellingOverlay } from './components/UpsellingOverlay';
import { linkToMergeFields, TAG_STYLES } from './constants';
import { useCustomFormats } from '../../services/hooks/editor/useCustomFormats';
import { useModules } from './hooks/useModules';
import { useSavedLimits } from './hooks/useSavedLimits';
import { useSavedList } from './hooks/useSavedList';
import { useShowTemplateIsBeingSavedToast } from './hooks/useShowTemplateIsBeingSavedToast';
import { useShowToasts } from './hooks/useShowToast';
import { useTemplateEditorActions } from '../../services/hooks/editor/useTemplateEditorActions';
import { usePrebuiltModulesList } from './prebuiltModules';
import { TemplateEditorProps } from './TemplateEditorInterface';
import { EditorType } from '@/services/dictionaries/EditorType';

// eslint-disable-next-line max-lines-per-function
export const TemplateEditor = ({
    editedTemplateName,
    editedTemplateIsPublic,
    useDraftScope = false,
    popupsMinimalZIndex = 10,
    useIndividualPaths = true,
    onSaveAndExitCallback,
    onBreadcrumbClick,
}: TemplateEditorProps): JSX.Element => {
    const dispatch = useDispatch();
    const { savedLimit, isSaveLocked, isSaveUpgradeable } = useSavedLimits();
    const [templateContent, setTemplateContent] = useState<EditorTemplateVersion>(null);
    const { t, i18n } = useTranslation();
    const { showModulesLimitReachedToast } = useShowToasts();
    const customFormats = useCustomFormats(EditorType.TemplateEditor);
    const { template: queryTemplateName } = useParams<{ template: string }>();
    const savedList = useSavedList();
    const specialFields = useEmailTemplateSpecialFields({});
    const { onModuleAdd, onModuleAddLocked, onModuleDelete, onModuleNameEdit, openModulesUpsellingOverlay } =
        useModules();
    const location = useLocation();
    const query = parseUrlParams<{ isPublic?: string }>(location.search);
    const isPublic = editedTemplateIsPublic || (query.isPublic as string) === 'true';
    const { boot, shutdown } = useIntercom() || {};
    const prebuiltModulesList = usePrebuiltModulesList();
    const dialog = useSelector(getDialogStore);
    const { showTemplateIsBeingSavedToast } = useShowTemplateIsBeingSavedToast();
    const {
        showSendTestModal,
        showEditTextBodyModal,
        showEditAmpBodyModal,
        showDefaultOptionsModal,
        showEditTagsModal,
        showSpecialFieldsModal,
        uploadFile,
        editorFilesUpdate,
        showOnceAITextToolModal,
    } = useTemplateEditorActions();
    const files = useSelector(getEditorFilesStore);
    const { brandColors, onBrandColorAdd, onBrandColorEdit, onBrandColorDelete } = useBrandColorsInTemplateEditor();  
    const { isLoaded: prebuildModulesListIsLoaded } = useSelector(getPrebuildModulesStore);
    const customFields = useSelector(getCustomFieldsStore);
    const { status, templateLoadedJson, templateName, autosaveCounter, isNewTemplate, recentColors, nameError } =
        useSelector(getEditorTemplateStore);
    const mediaManager = useSelector(getMediaManagerStore);
    const [showImageTabOnly, setShowImageTabOnly] = useState(false);
    const [isLinkToFile, setIsLinkToFile] = useState(false);
    const [currentBlockId, setCurrentBlockId] = useState<string>();
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);
    const { type } = useParams<{ type: CampaignCreatorType }>();
    const isAbCampaign = type === CampaignCreatorType.AB;
    const isRegularCampaign = type === CampaignCreatorType.REGULAR;
    const openMediaManager = (blockId: string, isLinkToFile: boolean): void => {
        setCurrentBlockId(blockId);
        setShowImageTabOnly(!isLinkToFile);
        setIsLinkToFile(isLinkToFile);
        dispatch(ShowMediaManager());
    };
    const handleBreadcrumbClick = (): void => {
        if (status === EditorTemplateStatus.SAVING) {
            showTemplateIsBeingSavedToast();
            return;
        }
        if (onBreadcrumbClick) {
            onBreadcrumbClick(!isNewTemplate ? templateName : null);
            return;
        }
        dispatch(CloseTemplateEditor());
        dispatch(Push(TemplatesPaths.TemplateList));
    };
    const previewTemplate = (templateHTML: string) => {
        setShowTemplatePreview(true);
        dispatch(SetTemplatePreviewHtml(templateHTML));
    };
    const pickedTemplateName = editedTemplateName || queryTemplateName;

    useEffect(() => {
        shutdown();
        if (pickedTemplateName) {
            dispatch(LoadStart(safelyDecodeBase64Url(pickedTemplateName), isPublic, useDraftScope));
        } else {
            dispatch(LoadStart('', isPublic, useDraftScope));
        }
        dispatch(GetContactFieldsStart({ isUrlUpdateDisabled: true }));
        if (!prebuildModulesListIsLoaded) {
            dispatch(PrebuildModulesLoadStart());
        }
        showOnceAITextToolModal();
        return () => {
            dispatch(Reset());
            isIntercomEnabled() && boot();
            shutdown?.();
            dispatch(GetContactFieldsStart({ isUrlUpdateDisabled: true }));
        };
    }, []);
    const onSave: EditorSaveEventHandler = (data: EditorSaveData, exitOnSave?: boolean): void => {
        dispatch(
            SaveStart({ isDraftTemplate: useDraftScope, useIndividualPaths, data, exitOnSave, onSaveAndExitCallback })
        );
    };

    const hideModal = () => {
        dispatch(HideDialog());
    };

    useEffect(() => {
        if (templateContent) {
            return;
        }
        if (customFields.isLoadedFields && !!templateLoadedJson && status === EditorTemplateStatus.LOADED) {
            setTemplateContent(updateTemplateContent(templateLoadedJson, status, TAG_STYLES));
        }
    }, [customFields.isLoadedFields, templateLoadedJson, status]);

    const localeForEmoji = getStoredLanguage();

    return (
        <>
            <Editor
                translations={
                    i18n.hasResourceBundle(i18n.language, 'template-editor')
                        ? i18n.getResourceBundle(i18n.language, 'template-editor')
                        : null
                }
                template={{
                    status,
                    name: templateName,
                    nameErrorMessage: nameError,
                    content: templateContent,
                }}
                events={{
                    onSendTest: showSendTestModal,
                    onFileUpload: uploadFile,
                    onMediaManagerOpen: openMediaManager,
                    onEditorFilesUpdate: editorFilesUpdate,
                    onEditTextBody: showEditTextBodyModal,
                    onEditAmpBody: showEditAmpBodyModal,
                    onEditDefaultOptions: showDefaultOptionsModal,
                    onEditTags: showEditTagsModal,
                    onSave,
                    onTemplatePreview: previewTemplate,
                    onBreadcrumbClick: handleBreadcrumbClick,
                    onShowSpecialFields: showSpecialFieldsModal,
                    onHideSpecialFields: hideModal,
                    onBrandColorAdd,
                    onBrandColorDelete,
                    onBrandColorEdit,
                    onModuleAdd,
                    onModuleDelete,
                    onModuleNameEdit,
                    onModuleLimitReached: showModulesLimitReachedToast,
                    onModuleAddLocked,
                }}
                media={{
                    mediaManagerComponent: {
                        content: (
                            <EditorMediaManager
                                showImageTabOnly={showImageTabOnly}
                                isLinkToFile={isLinkToFile}
                                blockId={currentBlockId}
                                isOpen={mediaManager.isOpened}
                            />
                        ),
                        isMediaManagerOpen: mediaManager.isOpened,
                    },
                    files,
                }}
                displayOptions={{
                    isBelowOtherLayer: dialog.isOpened,
                    popupsMinimalZIndex,
                }}
                autosaveOptions={{
                    counter: autosaveCounter,
                }}
                navigation={{
                    breadcrumbText: isRegularCampaign || isAbCampaign ? t('backtocampaign') : t('backtotemplates'),
                    isContinueButtonVariant: isRegularCampaign || isAbCampaign,
                }}
                linkToOptions={{ mergeFields: linkToMergeFields }}
                colorPickerOptions={{
                    recentColors,
                    brandColors,
                }}
                modules={{
                    savedList,
                    savedLimit,
                    savedLocked: isSaveLocked,
                    savedUpgradeable: isSaveUpgradeable,
                    savedLockedInfoComponent: (
                        <SavedLockedInfo
                            button={{
                                text: t('upgradetounlock'),
                                startIcon: <LockIcon width={20} height={20} />,
                                onClick: openModulesUpsellingOverlay,
                            }}
                        />
                    ),
                    prebuiltList: prebuiltModulesList,
                    nameMaxLength: 100,
                    savedModulesLimitInfo: <SavedModulesLimitInfo zIndex={popupsMinimalZIndex} />,
                }}
                specialFieldsOptions={specialFields.editorOptions}
                customFormats={customFormats}
                emojiOptions={{
                    locale: localeForEmoji as EmojiPickerLocale,
                }}
            />
            <PreviewOverlay isOpen={showTemplatePreview} setShowTemplatePreview={setShowTemplatePreview} />
            <UpsellingOverlay />
        </>
    );
};
